import type {
  RumActionEventDomainContext,
  RumErrorEvent,
  RumEvent,
} from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';
import { config } from 'config';

const findCustomActionName = (
  element?: HTMLElement,
  depth = 0,
): string | undefined => {
  if (!element || depth >= 10) {
    return undefined;
  }

  const actionName = element.getAttribute('telemetry-action-name');

  if (actionName) {
    return actionName;
  }

  // If not found, recursively search up the parent tree
  const parent = element.parentElement;

  if (parent) {
    return findCustomActionName(parent, depth + 1);
  }

  return undefined;
};

export const axiosNetworkErrorMessages = [
  'Async error when trying to login AxiosError: Network Error',
  'AxiosError: Network Error',
  'Exception captured: AxiosError: Network Error',
  'Failed to fetch feature flag assignments AxiosError: Network Error',
  'Network Error',
];

export const typeErrorNetworkErrorMessages = [
  'Error sending segment performance metrics TypeError: Failed to fetch',
  'Error sending segment performance metrics TypeError: Load failed',
  'TypeError: Failed to fetch',
  'TypeError: Load failed',
];

const isNetworkError = (event: RumErrorEvent) => {
  const type = event?.error?.type ?? '';
  const message = event?.error?.message ?? '';

  if (type === 'AxiosError' && axiosNetworkErrorMessages.includes(message)) {
    return true;
  }

  if (type === 'TypeError' && typeErrorNetworkErrorMessages.includes(message)) {
    return true;
  }
};

const isResizeObserverError = (event: RumErrorEvent) =>
  event?.error?.message ===
  'ResizeObserver loop completed with undelivered notifications.';

const isStripeLoadError = (event: RumErrorEvent) => {
  if (
    event?.error?.message === 'Failed to load Stripe.js' &&
    event?.error?.type === 'Error'
  ) {
    return true;
  }
};

const isInstagramError = (event: RumErrorEvent) => {
  if (
    event?.error?.message === "Can't find variable: _AutofillCallbackHandler"
  ) {
    return true;
  }
};

/**
 * This function is used to filter out errors from the Datadog RUM.
 * Learn more: https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#enrich-and-control-rum-data
 */
export function beforeSend(
  event: RumEvent,
  context: RumActionEventDomainContext,
) {
  if (
    event?.type === 'error' &&
    (isNetworkError(event) ||
      isResizeObserverError(event) ||
      isStripeLoadError(event) ||
      isInstagramError(event))
  ) {
    return false;
  } else if (event?.type === 'action' && event?.action?.type === 'click') {
    /**
     * We need to redact action names for clicks, by default, because it can
     * sometimes include PII. Example: a button with a label like "Edit Jane
     * Doe's Appointment".
     * Unfortunately, the target of the click is not necessarily the element
     * with an onClick event. For example, it may be a <span> inside a
     * <button>. The element with the onClick event is the one that we would
     * expect to have a telemetry-action-name attribute, so we have to look up
     * the parent elements until we find it.
     */
    const target = context?.events?.[0]?.target;
    const customActionName = findCustomActionName(target as HTMLElement);

    if (!event.action?.target?.name) {
      // Should never happen, but just in case
      return true;
    }

    if (customActionName) {
      event.action.target.name = customActionName;
    } else {
      event.action.target.name = 'Redacted';

      const targetId = (target as HTMLElement)?.id;
      const targetClass = (target as HTMLElement)?.className;

      if (targetId) {
        event.action.target.name += `#${targetId}`;
      } else if (targetClass) {
        event.action.target.name += `.${targetClass}`;
      }
    }
  }

  return true;
}

export const initDataDog = () => {
  if (
    !config.analytics.datadogClientToken ||
    !config.analytics.datadogApplicationId
  ) {
    return;
  }

  datadogRum.init({
    clientToken: config.analytics.datadogClientToken,
    applicationId: config.analytics.datadogApplicationId,
    site: 'datadoghq.com',
    service: 'core-web-booking',
    // Specify a version number to identify the deployed version of your application in Datadog
    version: __CODE_VERSION__,
    env: 'prod',
    sessionSampleRate: 1,
    sessionReplaySampleRate: 20,
    startSessionReplayRecordingManually: true,
    trackUserInteractions: true,
    defaultPrivacyLevel: 'mask',
    allowedTracingUrls: [/https:\/\/.*\.glossgenius.*\.com/],
    traceSampleRate: 25,
    beforeSend: beforeSend as any,
    actionNameAttribute: 'telemetry-action-name',
  });
};
